import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  eventTypesAPI,
  availabilityAPI,
  calendarConnectionsAPI,
} from "../services/schedulerAPI";
import { showToast } from "../components/Toast";

const CalendarSettings = () => {
  const [activeTab, setActiveTab] = useState("availability");
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // State for event types
  const [eventTypes, setEventTypes] = useState([]);
  const [showEventTypeModal, setShowEventTypeModal] = useState(false);
  const [currentEventType, setCurrentEventType] = useState(null);

  // State for availability
  const [availability, setAvailability] = useState([]);
  const [timezone, setTimezone] = useState("America/Los_Angeles");

  // State for calendar connections
  const [calendarConnections, setCalendarConnections] = useState([]);

  const tabs = [
    { id: "availability", name: "Availability", icon: "🕐" },
    { id: "event-types", name: "Event Types", icon: "📅" },
    { id: "calendars", name: "Connected Calendars", icon: "🔗" },
  ];

  const daysOfWeek = [
    { id: 0, name: "Monday", short: "Mon" },
    { id: 1, name: "Tuesday", short: "Tue" },
    { id: 2, name: "Wednesday", short: "Wed" },
    { id: 3, name: "Thursday", short: "Thu" },
    { id: 4, name: "Friday", short: "Fri" },
    { id: 5, name: "Saturday", short: "Sat" },
    { id: 6, name: "Sunday", short: "Sun" },
  ];

  useEffect(() => {
    loadData();
    // Handle OAuth callback
    handleOAuthCallback();
  }, []);

  const handleOAuthCallback = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      showToast.error(`OAuth error: ${error}`);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (code) {
      try {
        setLoading(true);
        await calendarConnectionsAPI.connectGoogleCalendar(code, state);
        await loadData();
        showSuccessMessage();
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('Failed to connect Google Calendar:', error);
        showToast.error('Failed to connect Google Calendar. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [eventTypesData, availabilityData, connectionsData] =
        await Promise.all([
          eventTypesAPI.list(),
          availabilityAPI.list(),
          calendarConnectionsAPI.list(),
        ]);

      setEventTypes(eventTypesData);
      setAvailability(availabilityData);
      setCalendarConnections(connectionsData);
    } catch (error) {
      console.error("Failed to load calendar settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAvailability = async (dayOfWeek, startTime, endTime) => {
    try {
      await availabilityAPI.create({
        timezone,
        day_of_week: dayOfWeek,
        start_time: startTime,
        end_time: endTime,
        is_available: true,
      });
      await loadData();
      showSuccessMessage();
    } catch (error) {
      console.error("Failed to save availability:", error);
      showToast.error("Failed to save availability. Please try again.");
    }
  };

  const handleDeleteAvailability = async (availabilityId) => {
    if (!window.confirm("Delete this availability slot?")) return;

    try {
      await availabilityAPI.delete(availabilityId);
      await loadData();
      showSuccessMessage();
    } catch (error) {
      console.error("Failed to delete availability:", error);
    }
  };

  const handleSaveEventType = async (eventTypeData) => {
    try {
      if (currentEventType?.id) {
        await eventTypesAPI.update(currentEventType.id, eventTypeData);
      } else {
        await eventTypesAPI.create(eventTypeData);
      }
      await loadData();
      setShowEventTypeModal(false);
      setCurrentEventType(null);
      showSuccessMessage();
    } catch (error) {
      console.error("Failed to save event type:", error);
      showToast.error("Failed to save event type. Please try again.");
    }
  };

  const handleDeleteEventType = async (eventTypeId) => {
    if (!window.confirm("Delete this event type?")) return;

    try {
      await eventTypesAPI.delete(eventTypeId);
      await loadData();
      showSuccessMessage();
    } catch (error) {
      console.error("Failed to delete event type:", error);
    }
  };

  const showSuccessMessage = () => {
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  // Availability Tab
  const AvailabilityTab = () => {
    const [editingDay, setEditingDay] = useState(null);
    const [startTime, setStartTime] = useState("09:00");
    const [endTime, setEndTime] = useState("17:00");

    const getAvailabilityForDay = (dayOfWeek) => {
      return availability.filter((a) => a.day_of_week === dayOfWeek);
    };

    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Set Your Availability
          </h2>
          <p className="text-gray-600">
            Define when you're available for bookings
          </p>
        </div>

        {/* Timezone Selector */}
        <div className="apple-glass p-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Timezone
          </label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="apple-input w-full"
          >
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="UTC">UTC</option>
            <option value="Europe/London">London (GMT)</option>
            <option value="Europe/Paris">Paris (CET)</option>
            <option value="Asia/Tokyo">Tokyo (JST)</option>
          </select>
        </div>

        {/* Weekly Schedule */}
        <div className="apple-card overflow-hidden">
          {daysOfWeek.map((day) => {
            const dayAvailability = getAvailabilityForDay(day.id);
            const isEditing = editingDay === day.id;

            return (
              <div
                key={day.id}
                className="border-b border-gray-200 last:border-b-0"
              >
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{day.name}</h3>
                    {dayAvailability.length > 0 ? (
                      <div className="mt-2 space-y-1">
                        {dayAvailability.map((slot) => (
                          <div
                            key={slot.id}
                            className="flex items-center gap-2 text-sm text-gray-600"
                          >
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                              {slot.start_time} - {slot.end_time}
                            </span>
                            <button
                              onClick={() => handleDeleteAvailability(slot.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 mt-1">Unavailable</p>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded"
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded"
                      />
                      <button
                        onClick={() => {
                          handleSaveAvailability(day.id, startTime, endTime);
                          setEditingDay(null);
                        }}
                        className="apple-btn-primary"
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingDay(null)}
                        className="apple-btn-secondary"
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingDay(day.id)}
                      className="px-4 py-2 text-primary-600 hover:bg-primary-50 rounded-lg font-medium"
                    >
                      Add Hours
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    );
  };

  // Event Types Tab
  const EventTypesTab = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Event Types
          </h2>
          <p className="text-gray-600">
            Create different types of bookable meetings
          </p>
        </div>
        <button
          onClick={() => {
            setCurrentEventType(null);
            setShowEventTypeModal(true);
          }}
          className="apple-btn-primary apple-hover-lift"
        >
          + New Event Type
        </button>
      </div>

      {/* Event Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {eventTypes.map((eventType) => (
          <motion.div
            key={eventType.id}
            whileHover={{ scale: 1.02 }}
            className="apple-card apple-hover-lift"
          >
            <div className="flex items-start justify-between mb-4">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: eventType.color }}
              ></div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setCurrentEventType(eventType);
                    setShowEventTypeModal(true);
                  }}
                  className="text-gray-500 hover:text-primary-600"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDeleteEventType(eventType.id)}
                  className="text-gray-500 hover:text-red-600"
                >
                  🗑️
                </button>
              </div>
            </div>

            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {eventType.name}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {eventType.description || "No description"}
            </p>

            <div className="flex flex-wrap gap-2 text-sm">
              <span className="apple-pill apple-pill-blue">
                {eventType.duration_minutes} min
              </span>
              {eventType.buffer_before_minutes > 0 && (
                <span className="apple-pill apple-pill-orange">
                  Buffer: {eventType.buffer_before_minutes}m before
                </span>
              )}
              {eventType.buffer_after_minutes > 0 && (
                <span className="apple-pill apple-pill-orange">
                  {eventType.buffer_after_minutes}m after
                </span>
              )}
              {!eventType.is_active && (
                <span className="apple-pill">
                  Inactive
                </span>
              )}
            </div>
          </motion.div>
        ))}

        {eventTypes.length === 0 && (
          <div className="col-span-2 text-center py-12 text-gray-500">
            <p className="text-lg mb-2">No event types yet</p>
            <p className="text-sm">
              Create your first event type to start accepting bookings
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );

  // Connected Calendars Tab
  const ConnectedCalendarsTab = () => {
    const [connectingGoogle, setConnectingGoogle] = useState(false);

    const handleConnectGoogle = async () => {
      try {
        setConnectingGoogle(true);
        const { auth_url } = await calendarConnectionsAPI.getGoogleAuthUrl();
        // Redirect to Google OAuth
        window.location.href = auth_url;
      } catch (error) {
        console.error('Failed to get Google auth URL:', error);
        showToast.error('Failed to initiate Google Calendar connection. Please check your configuration.');
        setConnectingGoogle(false);
      }
    };

    const handleDisconnectGoogle = async () => {
      if (!window.confirm('Disconnect Google Calendar? This will remove calendar sync.')) {
        return;
      }

      try {
        await calendarConnectionsAPI.disconnectGoogleCalendar();
        await loadData();
        showSuccessMessage();
      } catch (error) {
        console.error('Failed to disconnect Google Calendar:', error);
        showToast.error('Failed to disconnect Google Calendar.');
      }
    };

    const googleConnection = calendarConnections.find(c => c.provider === 'google');

    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Connected Calendars
          </h2>
          <p className="text-gray-600">
            Sync with Google Calendar and Apple Calendar
          </p>
        </div>

        {/* Connect Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {googleConnection ? (
            <div className="apple-card border-2 border-green-300 flex items-center gap-4 p-6">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600 text-2xl">
                ✓
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-gray-900">Google Calendar</h3>
                <p className="text-sm text-gray-600">{googleConnection.calendar_name}</p>
              </div>
              <button
                onClick={handleDisconnectGoogle}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg font-medium"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={handleConnectGoogle}
              disabled={connectingGoogle}
              className={`apple-card apple-hover-lift flex items-center gap-4 p-6 transition-all ${
                connectingGoogle ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <div className="w-12 h-12 apple-gradient-blue rounded-xl flex items-center justify-center text-white text-2xl">
                📆
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Google Calendar</h3>
                <p className="text-sm text-gray-600">
                  {connectingGoogle ? 'Connecting...' : 'Connect your Google account'}
                </p>
              </div>
            </button>
          )}

          <button className="apple-card apple-hover-lift flex items-center gap-4 p-6 transition-all opacity-50 cursor-not-allowed">
            <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl flex items-center justify-center text-white text-2xl">
              🍎
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Apple Calendar</h3>
              <p className="text-sm text-gray-600">Coming soon</p>
            </div>
          </button>
        </div>

      {/* Connected Calendars List */}
      {calendarConnections.length > 0 && (
        <div className="apple-card divide-y apple-divider">
          {calendarConnections.map((connection) => (
            <div key={connection.id} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600 font-semibold">
                  ✓
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">
                    {connection.calendar_name || connection.provider}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {connection.is_primary && (
                      <span className="text-primary-600 font-medium">
                        Primary •{" "}
                      </span>
                    )}
                    {connection.check_for_conflicts
                      ? "Checking conflicts"
                      : "Not checking conflicts"}
                  </p>
                </div>
              </div>
              <button
                onClick={async () => {
                  if (window.confirm("Disconnect this calendar?")) {
                    await calendarConnectionsAPI.delete(connection.id);
                    loadData();
                  }
                }}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                Disconnect
              </button>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

  // Event Type Modal
  const EventTypeModal = () => {
    const [formData, setFormData] = useState(
      currentEventType || {
        name: "",
        description: "",
        duration_minutes: 30,
        buffer_before_minutes: 0,
        buffer_after_minutes: 0,
        color: "#3B82F6",
        is_active: true,
      }
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="apple-modal apple-glass-strong max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">
              {currentEventType ? "Edit Event Type" : "Create Event Type"}
            </h2>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Event Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., 30 Min Meeting"
                className="apple-input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Describe this meeting type..."
                rows={3}
                className="apple-input w-full"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Duration (min)
                </label>
                <input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      duration_minutes: parseInt(e.target.value),
                    })
                  }
                  min="5"
                  max="480"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Buffer Before
                </label>
                <input
                  type="number"
                  value={formData.buffer_before_minutes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      buffer_before_minutes: parseInt(e.target.value),
                    })
                  }
                  min="0"
                  max="120"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Buffer After
                </label>
                <input
                  type="number"
                  value={formData.buffer_after_minutes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      buffer_after_minutes: parseInt(e.target.value),
                    })
                  }
                  min="0"
                  max="120"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Color
              </label>
              <input
                type="color"
                value={formData.color}
                onChange={(e) =>
                  setFormData({ ...formData, color: e.target.value })
                }
                className="w-full h-12 px-2 border border-gray-300 rounded-lg cursor-pointer"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">
                Active (can be booked)
              </label>
            </div>
          </div>

          <div className="p-6 border-t apple-divider flex gap-4">
            <button
              onClick={() => {
                setShowEventTypeModal(false);
                setCurrentEventType(null);
              }}
              className="flex-1 apple-btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSaveEventType(formData)}
              className="flex-1 apple-btn-primary"
            >
              Save Event Type
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Calendar Settings
          </h1>
          <p className="text-gray-600">
            Manage your availability and booking preferences
          </p>
        </div>

        {/* Success Message */}
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-xl flex items-center gap-3"
            >
              <span className="text-2xl">✓</span>
              <span className="font-semibold">Settings saved successfully!</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className="apple-glass-strong overflow-hidden">
          <div className="apple-divider flex overflow-x-auto" style={{ margin: 0 }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 font-semibold whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "border-b-2 border-primary-500 text-primary-600 bg-primary-50"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </div>

          <div className="p-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading...</p>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                {activeTab === "availability" && <AvailabilityTab />}
                {activeTab === "event-types" && <EventTypesTab />}
                {activeTab === "calendars" && <ConnectedCalendarsTab />}
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      {/* Event Type Modal */}
      {showEventTypeModal && <EventTypeModal />}
    </div>
  );
};

export default CalendarSettings;
